/**
 * Stable pastel assignment per interviewer for Schedule Interview (Details).
 * Mirrors KInterviewScheduler `SchInterviewerField` color map + index-based keys.
 */

const colorKeyByUserId: Record<number, string> = {};

const PASTEL_INDICES_WITHOUT_2 = [1, 3, 4, 5, 6, 7, 8, 9, 10] as const;

function sortedFiniteIds(ids: number[]): number[] {
  return [...new Set(ids)]
    .filter((id) => Number.isFinite(id) && id > 0)
    .sort((a, b) => a - b);
}

/**
 * Updates the module map for the current interviewer set (call before calendar + select render).
 * @param hasCandidateAvailability — when true, avoid assigning `pastel-2` (legacy parity).
 */
export function syncScheduleInterviewerPastelKeys(
  userIds: number[],
  hasCandidateAvailability = false
): void {
  const current = sortedFiniteIds(userIds);
  const currentSet = new Set(current);

  Object.keys(colorKeyByUserId).forEach((idStr) => {
    const id = Number(idStr);
    if (!currentSet.has(id)) {
      delete colorKeyByUserId[id];
    }
  });

  current.forEach((idNum, index) => {
    const existingColorKey = colorKeyByUserId[idNum];
    const mustSkipPastel2 =
      hasCandidateAvailability && existingColorKey === 'pastel-2';
    const colorKey =
      existingColorKey && !mustSkipPastel2
        ? existingColorKey
        : hasCandidateAvailability
          ? `pastel-${PASTEL_INDICES_WITHOUT_2[index % PASTEL_INDICES_WITHOUT_2.length]}`
          : `pastel-${(index % 10) + 1}`;
    colorKeyByUserId[idNum] = colorKey;
  });
}

/** Pastel key like `pastel-3` for chips / calendar after {@link syncScheduleInterviewerPastelKeys}. */
export function getScheduleInterviewerPastelKey(userId: number): string {
  if (!Number.isFinite(userId) || userId <= 0) {
    return 'pastel-1';
  }
  return colorKeyByUserId[userId] ?? 'pastel-1';
}

export function pastelNumberFromKey(key: string): number {
  const m = /^pastel-(\d+)$/.exec(key);
  const n = m ? Number(m[1]) : 1;
  if (!Number.isFinite(n) || n < 1 || n > 10) {
    return 1;
  }
  return n;
}
